import React from 'react';
import ContentBox from '../ContentBox';
import ColorPicker from '../ColorPicker';
import {
    useEmoteContext,
    EmotePickerModal,
    EmoteImg,
    PlainEmoteInput,
} from '../../services/emoteService';
import { useProfileSkins, isStaffOnlySkin, isSkinSaveable } from '../../data/profileSkins';

const _isBoxAsset = (a) => {
    const p = a.placement;
    if (!p) return true;
    if (Array.isArray(p)) return p.some(pl => pl === 'header' || pl === 'body');
    return p === 'header' || p === 'body';
};

const getBoxAssets = (skin) => (skin?.assets || []).filter(_isBoxAsset);

export { isSkinSaveable, isStaffOnlySkin };

const EditableBox = ({
                         boxKey, header, children, isEditing, isOwnProfile, getHeaderColour, setHeaderColour,
                         getBoxSkin, setBoxSkin,
                         dragEnabled, dragHandleProps, dragOver, editingHeader, setEditingHeader,
                         headerTitle, setHeaderTitle,
                         isStaff = false,
                         ownedSkinIds = [],
                         wrapperClassName = '',
                         ...props
                     }) => {

    const allSkins = useProfileSkins();
    const SKINS = React.useMemo(
        () => allSkins.filter(s => !s.staffOnly || isStaff),
        [allSkins, isStaff]
    );

    const headerColour = getHeaderColour(boxKey);
    const activeSkinId = getBoxSkin ? getBoxSkin(boxKey) : 'default';
    const activeSkin = SKINS.find(s => s.id === activeSkinId && s.id !== 'default') ?? null;
    const [tempTitle, setTempTitle] = React.useState("");
    const [isSkinPickerOpen, setIsSkinPickerOpen] = React.useState(false);
    const emoteCtx = useEmoteContext() ?? { packs: [], emotesByPack: {} };
    const [isHeaderEmotePickerOpen, setIsHeaderEmotePickerOpen] = React.useState(false);
    const headerEmoteInsertRef = React.useRef(null);

    const toggleSkinPicker = (e) => {
        e.stopPropagation();
        setIsSkinPickerOpen(v => !v);
    };

    React.useEffect(() => {
        if (!(isEditing && isOwnProfile)) setIsSkinPickerOpen(false);
    }, [isEditing, isOwnProfile]);
    const openHeaderEmotePicker = React.useCallback((insertFn) => {
        headerEmoteInsertRef.current = insertFn;
        setIsHeaderEmotePickerOpen(true);
    }, []);
    const closeHeaderEmotePicker = React.useCallback(() => setIsHeaderEmotePickerOpen(false), []);
    const handleHeaderEmoteSelected = React.useCallback((token) => {
        if (typeof headerEmoteInsertRef.current === "function") {
            headerEmoteInsertRef.current(String(token || ""));
        }
        setIsHeaderEmotePickerOpen(false);
    }, []);

    const startEditing = (e) => { e.stopPropagation(); setTempTitle(headerTitle || header); setEditingHeader(boxKey); };
    const saveTitle = (e) => { e.stopPropagation(); setHeaderTitle(boxKey, tempTitle); setEditingHeader(null); };
    const handleKeyDown = (e) => {
        if (e.key === "Enter") saveTitle(e);
        else if (e.key === "Escape") setEditingHeader(null);
    };

    const isEditingThisHeader = editingHeader === boxKey;
    const displayTitle = headerTitle || header;

    const headerTitleNodes = React.useMemo(() => {
        const text = String(displayTitle || "");
        const out = [];
        const re = /:(\d+):(\d+):/g;
        let last = 0;
        let m;
        while ((m = re.exec(text))) {
            if (m.index > last) out.push(text.slice(last, m.index));
            const pi = Number(m[1]);
            const ei = Number(m[2]);
            const packName = emoteCtx?.packs?.[pi - 1];
            const entry = packName ? emoteCtx?.emotesByPack?.[packName]?.[ei - 1] : null;
            const file = entry ? (typeof entry === "string" ? entry : (entry?.filename || "")) : "";
            if (packName && file) {
                out.push(
                    <EmoteImg
                        key={`h-${m.index}-${pi}-${ei}`}
                        packName={packName}
                        filename={file}
                        alt={m[0]}
                        className="inline-emote"
                        style={{ display: "inline-block", verticalAlign: "middle", height: "1em", width: "auto", imageRendering: "pixelated" }}
                    />
                );
            } else {
                out.push(m[0]);
            }
            last = m.index + m[0].length;
        }
        if (last < text.length) out.push(text.slice(last));
        return out;
    }, [displayTitle, emoteCtx]);

    const renderSkinAsset = (asset, i) => {
        const { image, anchor, offsets = {} } = asset;
        const style = { position: 'absolute', pointerEvents: 'none', zIndex: 2, imageRendering: 'pixelated' };
        if (anchor === 'top-right')         { style.top = `${offsets.top ?? 0}px`; style.right = `${offsets.right ?? 0}px`; }
        else if (anchor === 'top-left')     { style.top = `${offsets.top ?? 0}px`; style.left  = `${offsets.left  ?? 0}px`; }
        else if (anchor === 'top-center')   { style.top = `${offsets.top ?? 0}px`; style.left  = '50%'; style.transform = `translateX(calc(-50% + ${offsets.x ?? 0}px))`; }
        else if (anchor === 'bottom-right') { style.bottom = `${offsets.bottom ?? 0}px`; style.right = `${offsets.right ?? 0}px`; }
        else if (anchor === 'bottom-left')  { style.bottom = `${offsets.bottom ?? 0}px`; style.left  = `${offsets.left  ?? 0}px`; }
        else if (anchor === 'bottom-center'){ style.bottom = `${offsets.bottom ?? 0}px`; style.left  = '50%'; style.transform = `translateX(calc(-50% + ${offsets.x ?? 0}px))`; }
        return <img key={i} src={image} alt="" draggable={false} style={style} />;
    };

    return (
        <div className={`relative isolate mb-4 ${wrapperClassName}`}>
            <style>{(() => {
                const b = activeSkin?.border;
                const r = activeSkin?.borderRadius;
                const radiusCss = r ? ` border-radius: ${r} !important; overflow: hidden;` : '';
                const sel = `.editable-box-${boxKey}`;
                let borderCss = '';
                if (b && typeof b === 'object' && (b.gradient || b.animGradient)) {
                    const grad = b.animGradient || b.gradient;
                    const isAnim = !!b.animGradient;
                    const RAINBOW = ['#f4a8b0', '#f7c39b', '#f7e3a6', '#bce3b5', '#aed6ef', '#c5b6e8', '#e9b4d3'];
                    let colors = grad === 'rainbow' ? RAINBOW : (Array.isArray(grad) ? grad.filter(Boolean) : [grad]);
                    if (colors.length < 2) colors = [colors[0] || '#000000', colors[0] || '#000000'];
                    const stops = isAnim ? [...colors, colors[0]] : colors;
                    const gradientCss = `linear-gradient(90deg, ${stops.join(', ')})`;
                    const radius = r || '0px';
                    const anim = `franklyBorderFlow_${boxKey}`;
                    const keyframes = isAnim ? `@keyframes ${anim} { 0% { background-position: 0% 50%; } 100% { background-position: 200% 50%; } }` : '';
                    const flow = isAnim ? ` background-size: 200% 100%; animation: ${anim} 18s linear infinite;` : ' background-size: 100% 100%;';
                    const maskRing = `-webkit-mask: linear-gradient(#fff 0 0) content-box, linear-gradient(#fff 0 0); -webkit-mask-composite: xor; mask: linear-gradient(#fff 0 0) content-box, linear-gradient(#fff 0 0); mask-composite: exclude;`;
                    borderCss = `${keyframes} ${sel} { position: relative; border-width: 0 !important; box-shadow: 0 3px 0 0 rgba(0,0,0,0.5) !important; border-radius: ${radius} !important; overflow: hidden; } ${sel}::before { content: ''; position: absolute; inset: 0; padding: 2px; border-radius: ${radius}; background: ${gradientCss};${flow} ${maskRing} z-index: 1; pointer-events: none; } ${sel} > div.flex { position: relative; } ${sel} > div.flex::after { content: ''; position: absolute; left: 0; right: 0; bottom: 0; height: 2px; background: ${gradientCss};${flow} z-index: 1; pointer-events: none; }`;
                } else if (b && typeof b === 'object') {
                    const outerRing = b.outer ? `, 0 0 0 2px ${b.outer}` : '';
                    const outerHeaderRing = b.outer ? `, 0 2px 0 0 ${b.outer}` : '';
                    borderCss = ` ${sel} { border-color: ${b.inner} !important; box-shadow: 0 3px 0 0 rgba(0,0,0,0.5), 0 0 0 1px ${b.middle}${outerRing} !important;${radiusCss} } ${sel} > div.flex { border-bottom: 1px solid ${b.inner}; box-shadow: 0 1px 0 0 ${b.middle}${outerHeaderRing}; }`;
                } else if (b && typeof b === 'string') {
                    borderCss = ` ${sel} { border-color: ${b} !important;${radiusCss} } ${sel} > div.flex { border-bottom: 1px solid ${b}; }`;
                } else if (r) {
                    borderCss = ` ${sel} { ${radiusCss.trim()} }`;
                }
                return `${sel} > div.flex > h2 { flex-grow: 1; min-width: 0; } ${sel} { margin-bottom: 0 !important; }${borderCss}`;
            })()}</style>
            <ContentBox
                {...props}
                className={`editable-box-${boxKey} ${props.className || ""}`}
                contentClassName={`relative ${props.contentClassName || ""}`}
                header={
                    <div
                        className={`flex items-center justify-between w-full ${dragEnabled ? "cursor-move select-none" : ""}`}
                        {...(dragEnabled && !isEditingThisHeader ? dragHandleProps : {})}
                    >
                        {isEditingThisHeader ? (
                            <div
                                className="flex-1"
                                onPointerDown={(e) => e.stopPropagation()}
                                onMouseDown={(e) => e.stopPropagation()}
                                onClick={(e) => e.stopPropagation()}
                            >
                                <PlainEmoteInput
                                    value={tempTitle}
                                    onChange={setTempTitle}
                                    placeholder=""
                                    emoteCtx={emoteCtx}
                                    maxLength={80}
                                    disabled={false}
                                    multiLine={false}
                                    className="bg-white/20 border border-white/40 rounded px-2 py-1 text-inherit outline-none focus:bg-white/30 focus:border-white/60"
                                    onKeyDown={handleKeyDown}
                                    onOpenEmotePicker={openHeaderEmotePicker}
                                />
                            </div>
                        ) : (
                            <span className="flex-1 truncate pointer-events-none">{headerTitleNodes}</span>
                        )}

                        <div className="relative z-30 flex items-center gap-2 ml-2 shrink-0">
                            {isEditing && isOwnProfile && !isEditingThisHeader && (
                                <button type="button" onClick={startEditing} onPointerDown={(e) => e.stopPropagation()} onMouseDown={(e) => e.stopPropagation()} className="h-6 w-6 flex items-center justify-center hover:bg-white/20 rounded transition-colors" title="Edit header title">
                                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M17 3a2.828 2.828 0 1 1 4 4L7.5 20.5 2 22l1.5-5.5L17 3z" /></svg>
                                </button>
                            )}
                            {isEditingThisHeader && (
                                <button type="button" onClick={saveTitle} onPointerDown={(e) => e.stopPropagation()} onMouseDown={(e) => e.stopPropagation()} className="h-6 w-6 flex items-center justify-center hover:bg-white/20 rounded transition-colors" title="Save header title">
                                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><polyline points="20 6 9 17 4 12" /></svg>
                                </button>
                            )}
                            {isEditing && isOwnProfile && (
                                <ColorPicker
                                    value={headerColour}
                                    onChange={(e) => setHeaderColour(boxKey, e.target.value)}
                                    className="h-6 w-6 p-0 border border-black/50 rounded cursor-pointer"
                                    title="Header colour"
                                    icon={
                                        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ color: '#fff' }}>
                                            <path d="M12 2C6.5 2 2 6 2 11c0 4 3 6 6 6 1 0 2-.5 2-1.5 0-.5-.2-.8-.5-1.2-.3-.4-.5-.7-.5-1.3 0-1 .8-1.5 1.8-1.5H14c3.3 0 6-2.2 6-5.5C20 4.8 16.4 2 12 2z" />
                                            <circle cx="6.5" cy="11.5" r="1" />
                                            <circle cx="9.5" cy="7.5" r="1" />
                                            <circle cx="14.5" cy="7.5" r="1" />
                                            <circle cx="17.5" cy="11.5" r="1" />
                                        </svg>
                                    }
                                />
                            )}
                            {isEditing && isOwnProfile && setBoxSkin && SKINS.length > 1 && (
                                <button
                                    type="button"
                                    onClick={toggleSkinPicker}
                                    onPointerDown={(e) => e.stopPropagation()}
                                    onMouseDown={(e) => e.stopPropagation()}
                                    className={`h-6 px-2 flex items-center gap-1 border border-black/50 rounded transition-colors text-sm font-['Jersey_10'] uppercase tracking-widest leading-none shrink-0 text-white ${isSkinPickerOpen ? 'bg-neutral-800/80' : 'bg-neutral-800/50 hover:bg-neutral-800/70'}`}
                                    title="Box skin"
                                >
                                    <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                        <polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2" />
                                    </svg>
                                    Skin
                                </button>
                            )}
                            {dragEnabled && !isEditingThisHeader && (
                                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" className="text-white/80 shrink-0">
                                    <path d="M12 3v18M3 12h18" /><path d="M15 6l-3-3-3 3M15 18l-3 3-3-3M6 9l-3 3 3 3M18 9l3 3-3 3" />
                                </svg>
                            )}
                        </div>
                    </div>
                }
                tint={headerColour || props.tint}
                headerBackground={activeSkin?.backgroundImage || undefined}
                headerBackgroundMode={activeSkin?.backgroundMode || undefined}
                headerBackgroundVerticalOffset={activeSkin?.verticalOffset || undefined}
            >
                {children}
                <EmotePickerModal
                    open={isHeaderEmotePickerOpen}
                    onClose={closeHeaderEmotePicker}
                    onSelect={handleHeaderEmoteSelected}
                    emoteCtx={emoteCtx}
                />
                {isSkinPickerOpen && (
                    <div
                        className="absolute inset-0 z-[210] flex flex-col bg-offwhite border-2 border-coal shadow-[0_3px_0_0_rgba(0,0,0,0.5)]"
                        onPointerDown={(e) => e.stopPropagation()}
                        onMouseDown={(e) => e.stopPropagation()}
                        onClick={(e) => e.stopPropagation()}
                    >
                        <p className="px-3 py-2 text-md leading-snug text-coal font-['Jersey_10'] uppercase tracking-widest border-b border-gray-300 shrink-0">
                            Pick a skin for this box. Don't own one? You can still preview it on your profile, but you can't save it.
                        </p>
                        <div className="flex-1 min-h-0 overflow-y-auto habbo-scroll-v" style={{ containerType: 'inline-size' }}>
                        <style>{`
                            .skin-picker-grid { grid-template-columns: repeat(3, minmax(0, 1fr)); }
                            @container (min-width: 420px) { .skin-picker-grid { grid-template-columns: repeat(4, minmax(0, 1fr)); } }
                            @container (min-width: 520px) { .skin-picker-grid { grid-template-columns: repeat(5, minmax(0, 1fr)); } }
                            @container (min-width: 620px) { .skin-picker-grid { grid-template-columns: repeat(6, minmax(0, 1fr)); } }
                        `}</style>
                        <div className="skin-picker-grid p-2 grid content-start gap-2">
                            {SKINS.map(skin => {
                                const saveable = isSkinSaveable(skin.id, isStaff, ownedSkinIds);
                                const isPreview = !saveable;

                                return (
                                    <button
                                        key={skin.id}
                                        type="button"
                                        onClick={(e) => { e.stopPropagation(); setBoxSkin(boxKey, skin.id); setIsSkinPickerOpen(false); }}
                                        className={`flex flex-col items-center gap-1 p-1 w-full min-w-0 hover:bg-black/5 transition-colors ${activeSkinId === skin.id ? 'outline outline-2 outline-coal' : ''}`}
                                        title={isPreview ? `${skin.name} (preview - you don't own this skin yet)` : skin.name}
                                    >
                                        <div
                                            className="w-10 h-10 border border-gray-300 overflow-hidden relative shrink-0"
                                            style={{
                                                ...(skin.backgroundImage && skin.backgroundMode !== 'overlay' ? { backgroundImage: `url(${skin.backgroundImage})`, backgroundSize: 'cover', backgroundPosition: 'center' } : {}),
                                                backgroundColor: skin.backgroundMode === 'overlay' ? '#e5e7eb' : (!skin.backgroundImage ? '#e5e7eb' : undefined),
                                            }}
                                        >
                                            {skin.backgroundMode === 'overlay' && skin.backgroundImage && (
                                                <img src={skin.backgroundImage} alt="" style={{ position: 'absolute', top: '50%', left: '50%', transform: 'translate(-50%,-50%)', imageRendering: 'pixelated', maxWidth: 'none' }} />
                                            )}
                                            {getBoxAssets(skin)[0] && (
                                                <img src={getBoxAssets(skin)[0].image} alt="" style={{ position: 'absolute', top: '50%', left: '50%', transform: 'translate(-50%,-50%)', imageRendering: 'pixelated', maxWidth: 'none' }} />
                                            )}
                                            {/* PREVIEW stamp – purely visual, does not block selection */}
                                            {isPreview && (
                                                <div style={{
                                                    position: 'absolute', inset: 0,
                                                    backgroundColor: 'rgba(255,255,255,0.45)',
                                                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                                                    overflow: 'hidden', pointerEvents: 'none',
                                                }}>
                                                <span style={{
                                                    transform: 'rotate(-35deg)',
                                                    fontSize: '14px',
                                                    fontFamily: "'Jersey 10', sans-serif",
                                                    fontWeight: 900,
                                                    letterSpacing: '0.02em',
                                                    color: 'rgba(0,0,0,0.82)',
                                                    textTransform: 'uppercase',
                                                    whiteSpace: 'nowrap',
                                                    lineHeight: 1,
                                                    userSelect: 'none',
                                                }}>
                                                    PREVIEW
                                                </span>
                                                </div>
                                            )}
                                        </div>
                                        <span className="w-full truncate text-center text-sm text-coal font-['Jersey_10'] uppercase leading-none tracking-widest">{skin.name}</span>
                                    </button>
                                );
                            })}
                        </div>
                        </div>
                    </div>
                )}
            </ContentBox>
            {getBoxAssets(activeSkin).map((asset, i) => renderSkinAsset(asset, i))}
        </div>
    );
};

export default EditableBox;